import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemTitle,
} from "@/client/components/shad/item";
import { useSilosSelection } from "./hooks/useSilos";
import { getSpeedInKnots } from "@/client/lib/getSpeedInKnots";
import { Button } from "@/client/components/shad/button";
import { ButtonGroup } from "@/client/components/shad/button-group";
import {
  IconBox,
  IconChevronDown,
  IconCircleCheck,
  IconDeselect,
  IconDrone,
  IconGlobe,
  IconHomeStats,
  IconLock,
  IconPlant,
  IconRadio,
  IconRadioactive,
  IconRadiusBottomLeft,
  IconRocket,
  IconSelect,
  IconStatusChange,
} from "@tabler/icons-react";
import { getAltitudeInFeet } from "@/client/lib/getAltitudeInFeet";
import { getHeadingInDegrees } from "@/client/lib/getHeadingInDegrees";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/client/components/shad/collapsible";
import type { SiloPlatform, SkopaMissileSilo } from "@/client/map/types";
import { cn } from "@/client/lib/utils";
import { Badge } from "@/client/components/shad/badge";
import { useState } from "react";

export const SkopaSilos = () => {
  // 🌟 CALL THE HOOK EXACTLY ONCE HERE AT THE PARENT ROOT LAYER
  const { selectedTargets, deselect } = useSilosSelection();

  return (
    <div className="h-full flex flex-col">
      {selectedTargets.map((platform) => {
        // Calculate selection flag at parent level
        const isSelected = selectedTargets.some(
          (target) => target.platform_id === platform.platform_id,
        );

        return (
          <SkopaSilosItems
            key={platform.platform_id}
            platform={platform}
            onDeselect={() => deselect(platform.platform_id)}
          />
        );
      })}
    </div>
  );
};

// 🌟 SUB-COMPONENT: A clean, stateless pure presentational view layer
interface SkopaSilosItemsProps {
  platform: SiloPlatform;
  onDeselect: () => void;
}

const SkopaSilosItems = ({ platform, onDeselect }: SkopaSilosItemsProps) => {
  return (
    <Collapsible open={true}>
      <Item className="border-none px-0">
        <ItemContent className="font-sans">
          <ItemTitle className={cn("line-clamp-1 flex items-center")}>
            {platform.name}
          </ItemTitle>
        </ItemContent>
        <ItemActions>
          <ButtonGroup>
            <Button onClick={onDeselect} variant={"outline"} size="xs">
              <IconDeselect /> Drop
            </Button>
            {/*<Button variant={"outline"} size="icon-xs">
              <IconLock />
            </Button>*/}
          </ButtonGroup>
        </ItemActions>
        <ItemFooter className="-mt-1 block border-b pb-2">
          <CollapsibleContent className="font-mono text-xs text-muted-foreground brightness-140 col-span-full grid grid-cols-subgrid gap-2 mt-3">
            <dl className="grid grid-cols-2">
              <dt>Status:</dt>
              <dd>
                {platform.health_state && (
                  <IconCircleCheck className="size-4 text-lime-500/70 saturate-50 " />
                )}
              </dd>
            </dl>
            <dl className="grid grid-cols-2">
              <dt>Speed:</dt>
              <dd>{getSpeedInKnots(platform.speed_knots)}</dd>
            </dl>
            <dl className="grid grid-cols-2">
              <dt>Heading:</dt>
              <dd>{getHeadingInDegrees(platform.heading)}</dd>
            </dl>
            <dl className="grid grid-cols-2">
              <dt>Range:</dt>
              <dd>{platform.combat_range_meters}</dd>
            </dl>
            <dl className="grid grid-cols-2">
              <dt>Type:</dt>
              <dd>{platform.type}</dd>
            </dl>

            <div className="">
              {platform.silos.map((silo) => {
                return <SkopaSilosItemsSilo key={silo.silo_id} silo={silo} />;
              })}
            </div>
          </CollapsibleContent>
        </ItemFooter>
      </Item>
    </Collapsible>
  );
};

const SkopaSilosItemsSilo = ({ silo }: { silo: SkopaMissileSilo }) => {
  const [isOpen, setIsOpen] = useState(false);

  const skopaCapacity = Array.from({ length: silo.capacity });

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <header className="pt-2 flex items-center">
          <div className="flex gap-2 items-center grow">
            <IconBox className="size-4" />
            <strong>{silo.silo_id}</strong>
          </div>
          <Button variant="ghost">
            <IconChevronDown />
          </Button>
        </header>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-1.5 pl-4.5 space-y-1.5 border-l">
          <dl className="grid grid-cols-2">
            <dt>Temp:</dt>
            <dd>{silo.ambient_temp_c}</dd>
          </dl>
          <dl className="grid grid-cols-2">
            <dt>Status:</dt>
            <dd>{silo.status}</dd>
          </dl>
          <dl className="grid grid-cols-2">
            <dt>Cooling:</dt>
            <dd>{silo.cooling_power_kw}</dd>
          </dl>
          <dl className="grid grid-cols-2">
            <dt>Capacity</dt>
            <dd>{silo.capacity}</dd>
          </dl>

          <div className="flex gap-2">
            {skopaCapacity.map((_, index) => {
              const hasRocket = index < silo.drones.length;

              if (hasRocket) {
                return (
                  <div className="flex gap-1 items-center border p-1 rounded-sm">
                    <IconRocket className="size-4 text-lime-500/70 saturate-30" />
                  </div>
                );
              }

              return (
                <div className="flex cursor-not-allowed gap-1 items-center border p-1 rounded-sm">
                  <IconRocket className="size-4 text-zinc-500/70 saturate-30" />
                </div>
              );
            })}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
